import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Printer, CheckCircle2, Download, Loader2 } from "lucide-react";
import { numberToWordsINR } from "@/lib/numberToWords";

export default function MaintenanceReceiptModal({ bill, onClose }) {
    const printRef = useRef(null);
    const [downloading, setDownloading] = useState(false);

    if (!bill) return null;

    const receiptNo = bill.receipt_no || (bill.receipt_no_int ? String(bill.receipt_no_int) : "5378");
    const societyName = bill.society_name || "Pyramid City 3 Owners Co-Operative Housing Society Maryadit";
    const regnNo = bill.society_regn || "NGP/ARN/HCG(TC)/1709/2021";
    const address = bill.society_address || "Besa-Pipla Road, Pipla, Nagpur (Maharashtra) - 440 037.";
    const residentName = bill.paid_by_name || bill.resident_name || "Shrutika / Tilak Kalbande";
    const flatStr = bill.tower && bill.flat_no ? `${bill.tower}-${bill.flat_no}` : (bill.flat_no || "Flat");
    const period = bill.period || "Nov-26";
    const amount = Number(bill.amount || 0);
    const amountInWords = numberToWordsINR(amount);
    const utrNo = bill.utr_no || "Online UPI";
    const paymentDate = bill.paid_at ? new Date(bill.paid_at).toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" }) : new Date().toLocaleDateString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric" });

    // Download authentic PDF generated by backend
    const handleDownloadPdf = async () => {
        setDownloading(true);
        try {
            const res = await api.get(`/bills/${bill.bill_id}/receipt-pdf`, {
                responseType: "blob"
            });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.download = `Maintenance_Receipt_${receiptNo}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(() => window.URL.revokeObjectURL(url), 1000);
            toast.success("Receipt PDF downloaded!");
        } catch (err) {
            // Direct browser download / fallback tab
            const token = localStorage.getItem("token") || "";
            const pdfUrl = `${api.defaults.baseURL || "/api"}/bills/${bill.bill_id}/receipt-pdf?token=${encodeURIComponent(token)}`;
            window.open(pdfUrl, "_blank");
        } finally {
            setDownloading(false);
        }
    };

    // Clean print that works across mobile Safari, Android Chrome, and Desktop
    const handlePrint = () => {
        const printable = document.getElementById("printable-society-receipt");
        if (!printable) {
            window.print();
            return;
        }

        const printWindow = window.open("", "_blank");
        if (!printWindow) {
            window.print();
            return;
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>Official Maintenance Receipt #${receiptNo}</title>
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <script src="https://cdn.tailwindcss.com"></script>
                <style>
                    @page { size: auto; margin: 10mm; }
                    body { margin: 0; padding: 12px; background: white; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                </style>
            </head>
            <body>
                <div style="max-width: 650px; margin: 0 auto;">
                    ${printable.outerHTML}
                </div>
                <script>
                    window.onload = function() {
                        window.focus();
                        window.print();
                        setTimeout(function() { window.close(); }, 1500);
                    };
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/70 backdrop-blur-xs overflow-y-auto animate-fade-in print:p-0 print:bg-white">
            <style>{`
                @media print {
                    body * {
                        visibility: hidden;
                    }
                    #printable-society-receipt, #printable-society-receipt * {
                        visibility: visible;
                    }
                    #printable-society-receipt {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        margin: 0;
                        padding: 24px;
                        border: 2px solid #000 !important;
                        box-shadow: none !important;
                    }
                    .no-print {
                        display: none !important;
                    }
                }
            `}</style>

            <div className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl border border-nest-line overflow-hidden my-auto print:border-0 print:shadow-none print:rounded-none">
                {/* Modal Top Control Bar (Hidden during print) */}
                <div className="no-print flex items-center justify-between px-4 sm:px-5 py-3.5 bg-nest-base/60 border-b border-nest-line">
                    <div className="flex items-center gap-2">
                        <span className="p-1 rounded-lg bg-emerald-100 text-emerald-800">
                            <CheckCircle2 size={16} />
                        </span>
                        <p className="font-bold text-xs sm:text-sm text-nest-ink">
                            Official Maintenance Receipt #{receiptNo}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={handleDownloadPdf}
                            disabled={downloading}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold transition cursor-pointer active:scale-95 shadow-2xs disabled:opacity-50"
                            title="Download Official PDF"
                        >
                            {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
                            <span>Download PDF</span>
                        </button>
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl border border-nest-line hover:border-nest-ink bg-white text-xs font-bold text-nest-ink transition cursor-pointer active:scale-95 shadow-2xs"
                            title="Print Receipt"
                        >
                            <Printer size={13} className="text-nest-clay" />
                            <span className="hidden sm:inline">Print</span>
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-1.5 rounded-xl text-nest-ink3 hover:text-nest-ink hover:bg-nest-line transition cursor-pointer"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* The Exact Physical Receipt Recreated in Clean HTML/CSS */}
                <div id="printable-society-receipt" ref={printRef} className="p-5 sm:p-8 bg-[#FFFEFA] border-4 border-amber-950/20 text-neutral-900 font-sans relative">
                    {/* Watermark PAID Stamp - Centered in Middle */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none select-none z-10">
                        <div className="opacity-15 rotate-[-22deg] border-4 sm:border-6 border-emerald-700 text-emerald-800 rounded-3xl px-8 sm:px-14 py-3 sm:py-5 font-black text-4xl sm:text-6xl tracking-widest uppercase shadow-sm">
                            PAID ✓
                        </div>
                    </div>

                    {/* Top Header Line */}
                    <div className="flex items-start justify-between gap-2 text-[11px] sm:text-xs font-bold text-neutral-700 pb-1">
                        <div>
                            <span>Regn. No. : </span>
                            <span className="font-mono text-neutral-950">{regnNo}</span>
                        </div>
                        <div className="text-right">
                            <span className="font-black text-sm sm:text-base tracking-widest uppercase text-neutral-900 underline decoration-2">
                                RECEIPT
                            </span>
                            <p className="text-[10px] font-mono text-emerald-700">Online - UPI</p>
                        </div>
                    </div>

                    {/* Society Big Title */}
                    <div className="text-center mt-1 sm:mt-2">
                        <h2 className="font-display font-black text-xl sm:text-2xl md:text-3xl text-rose-700 tracking-tight leading-tight">
                            {societyName}
                        </h2>
                        {/* Address Bar */}
                        <div className="mt-1.5 py-1 px-2 bg-neutral-900 text-white rounded text-[10px] sm:text-xs font-medium tracking-wide">
                            {address}
                        </div>
                    </div>

                    {/* Receipt No & Date */}
                    <div className="flex items-center justify-between mt-4 pb-2 border-b-2 border-neutral-300">
                        <div className="flex items-baseline gap-2">
                            <span className="font-bold text-xs sm:text-sm text-neutral-800">Receipt No. :</span>
                            <span className="font-mono font-black text-base sm:text-lg text-rose-700 tracking-wide">
                                {receiptNo}
                            </span>
                        </div>
                        <div className="flex items-baseline gap-2">
                            <span className="font-bold text-xs sm:text-sm text-neutral-800">Date :</span>
                            <span className="font-mono font-bold text-xs sm:text-sm text-neutral-900 border-b border-dotted border-neutral-600 px-2 pb-0.5">
                                {paymentDate}
                            </span>
                        </div>
                    </div>

                    {/* Receipt Body Fields (Lined Paper Style) */}
                    <div className="mt-4 space-y-3 sm:space-y-4 text-xs sm:text-sm">
                        {/* Received with thanks from */}
                        <div className="flex flex-wrap items-baseline gap-2 leading-relaxed">
                            <span className="text-neutral-700 whitespace-nowrap">Received With Thanks From Mr. / Mrs. :</span>
                            <span className="font-bold text-neutral-950 flex-1 border-b border-dotted border-neutral-500 pb-0.5 min-w-[200px]">
                                {residentName}
                            </span>
                            <span className="font-mono font-bold text-neutral-950 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded text-xs">
                                Flat {flatStr}
                            </span>
                        </div>

                        {/* For / Purpose */}
                        <div className="flex flex-wrap items-baseline gap-2 leading-relaxed">
                            <span className="text-neutral-700 whitespace-nowrap">For :</span>
                            <span className="font-semibold text-neutral-900 border-b border-dotted border-neutral-500 pb-0.5">
                                Maintenance Fund / Society Charges
                            </span>
                            <span className="text-neutral-700 ml-auto whitespace-nowrap">Period :</span>
                            <span className="font-bold text-rose-800 border-b border-dotted border-neutral-500 pb-0.5 px-2">
                                {period}
                            </span>
                        </div>

                        {/* Amount in Words */}
                        <div className="flex items-baseline gap-2 leading-relaxed">
                            <span className="text-neutral-700 whitespace-nowrap">Rs. (in words) :</span>
                            <span className="font-bold text-neutral-950 italic flex-1 border-b border-dotted border-neutral-500 pb-0.5">
                                {amountInWords}
                            </span>
                        </div>

                        {/* Payment Mode / UTR Reference */}
                        <div className="flex flex-wrap items-baseline gap-2 leading-relaxed">
                            <span className="text-neutral-700 whitespace-nowrap">Online Transfer / UPI Ref (UTR) :</span>
                            <span className="font-mono font-black text-neutral-950 flex-1 border-b border-dotted border-neutral-500 pb-0.5 tracking-wider">
                                {utrNo}
                            </span>
                            <span className="text-[11px] text-emerald-700 font-bold bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                                Confirmed
                            </span>
                        </div>
                    </div>

                    {/* Bottom Box & Signatures */}
                    <div className="mt-6 pt-4 border-t-2 border-neutral-300 flex flex-col sm:flex-row items-center justify-between gap-4">
                        {/* Amount Box */}
                        <div className="border-2 border-neutral-900 rounded-xl px-4 py-2 bg-white shadow-2xs flex items-center gap-2">
                            <span className="font-black text-lg sm:text-2xl text-neutral-950">₹</span>
                            <span className="font-mono font-black text-xl sm:text-2xl text-neutral-950 tracking-tight">
                                {amount.toFixed(2)}/-
                            </span>
                        </div>

                        <p className="text-[10px] text-neutral-500 italic hidden sm:block">
                            Subject To Realisation Of Online Payment
                        </p>

                        {/* Signatures & Receiver Name */}
                        <div className="flex items-end gap-6 text-center">
                            <div>
                                <div className="h-9 flex items-center justify-center italic font-serif text-neutral-400 text-sm">
                                    [Online Verified]
                                </div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-600 border-t border-neutral-400 pt-1">
                                    Resident Copy
                                </p>
                            </div>
                            <div>
                                <div className="h-9 flex items-center justify-center font-bold text-rose-800 text-xs tracking-wider">
                                    Pyramid City 3
                                </div>
                                <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-900 border-t border-neutral-900 pt-1">
                                    Receiver Name
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Modal Bottom Actions (Hidden during print) */}
                <div className="no-print p-4 sm:p-5 bg-white border-t border-nest-line flex flex-col sm:flex-row items-center justify-between gap-3">
                    <div className="text-xs text-nest-ink3 text-center sm:text-left">
                        Receipt generated electronically on {paymentDate} · Verified by NeighborNest
                    </div>

                    <div className="flex items-center gap-2.5 w-full sm:w-auto">
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="flex-1 sm:flex-none py-2.5 px-4 rounded-xl border border-nest-line hover:border-nest-ink bg-white text-xs sm:text-sm font-bold text-nest-ink flex items-center justify-center gap-2 transition cursor-pointer active:scale-95 shadow-2xs"
                        >
                            <Printer size={15} className="text-nest-clay" />
                            <span>Print</span>
                        </button>
                        <button
                            type="button"
                            onClick={handleDownloadPdf}
                            disabled={downloading}
                            className="flex-1 sm:flex-none py-2.5 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs sm:text-sm font-bold flex items-center justify-center gap-2 transition cursor-pointer active:scale-95 shadow-xs disabled:opacity-50"
                        >
                            {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                            <span>Download PDF</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
